import { inject, observer } from 'mobx-react';
import React from 'react';

import useI18n from 'hooks/use-i18n';

import Counter from './Counter';

import WidgetCVEProgression from '../progression';
import WidgetTweets from '../social';

/** @type {React.VFC<any>} */
const WidgetBulletins = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { bulletins },
                statsLoading
            },
            classes
        }) => {
            const i18n = useI18n();
            return (
                <Counter
                    data={{
                        name: 'BULLETINS',
                        text: i18n('statsCounterBulletins'),
                        value: bulletins > 999999 ? (bulletins / 1000000).toFixed() + 'mln +' : bulletins,
                        key: 'type:*'
                    }}
                    loading={statsLoading}
                    customClasses={classes}
                />
            );
        }
    )
);

/** @type {React.VFC<any>} */
const WidgetVendors = inject('statsStore')(
    observer(
        ({
            statsStore: {
                types,
                stats: { collections },
                statsLoading
            },
            classes
        }) => {
            const i18n = useI18n();

            return (
                <Counter
                    data={{
                        name: 'VENDORS',
                        text: i18n('statsCounterVendors'),
                        value: Number(collections) || Object.keys(types || {}).length || 0,
                        key: 'type:cve'
                    }}
                    loading={statsLoading}
                    customClasses={classes}
                />
            );
        }
    )
);

/** @type {React.VFC<any>} */
const WidgetExploits = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { exploits },
                statsLoading
            },
            classes
        }) => {
            const i18n = useI18n();

            return (
                <Counter
                    data={{
                        name: 'EXPLOITS',
                        text: i18n('statsCounterExploits'),
                        value: (exploits / 1000).toFixed() + 'k +',
                        key: 'bulletinFamily:exploit'
                    }}
                    loading={statsLoading}
                    customClasses={classes}
                />
            );
        }
    )
);

/** @type {React.VFC<any>} */
const WidgetScore = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { avgCvssScore },
                statsLoading
            },
            classes
        }) => {
            const i18n = useI18n();

            return (
                <Counter
                    data={{
                        name: 'CVSS SCORE',
                        text: i18n('statsCounterScore'),
                        value: (avgCvssScore || 0).toFixed(2),
                        key: 'cvss.score:10'
                    }}
                    loading={statsLoading}
                    customClasses={classes}
                />
            );
        }
    )
);

/** @type {React.VFC<any>} */
const WidgetEntriesPerDay = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { avgBulletinsPerDay },
                statsLoading
            },
            classes
        }) => {
            return (
                <Counter
                    data={{
                        name: 'Entries per day',
                        text: 'Average Entries added per day',
                        value: (avgBulletinsPerDay || 0).toFixed(0),
                        key: 'order:published last 1 days'
                    }}
                    loading={statsLoading}
                    customClasses={classes}
                />
            );
        }
    )
);

/** @type {React.VFC<any>} */
const WidgetCVEPerDay = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { avgCvePerDay },
                statsLoading
            },
            classes
        }) => (
            <Counter
                data={{
                    name: 'CVE per day',
                    text: 'Average CVE released per day',
                    value: (avgCvePerDay || 0).toFixed(0),
                    key: 'type:cve last 1 days'
                }}
                loading={statsLoading}
                customClasses={classes}
            />
        )
    )
);

/** @type {React.VFC<any>} */
const WidgetExploitAppearHours = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { avgExploitPublicationDate },
                statsLoading
            },
            classes
        }) => (
            <Counter
                data={{
                    name: 'Hours exploit appear',
                    text: 'Average hours to exploit published',
                    value: (avgExploitPublicationDate / (60 * 60)).toFixed() + 'h',
                    key: 'bulletinFamily:exploit last 7 days'
                }}
                loading={statsLoading}
                customClasses={classes}
            />
        )
    )
);

/** @type {React.VFC<any>} */
const WidgetWildExploited = inject('statsStore')(
    observer(
        ({
            statsStore: {
                stats: { avgWildExploitPublished },
                statsLoading
            },
            classes
        }) => (
            <Counter
                data={{
                    name: 'Days exploited in the wild',
                    text: 'Average days to cve exploited in the wild',
                    value: (avgWildExploitPublished / (60 * 60 * 24)).toFixed() + 'd',
                    key: 'enchantments.exploitation.wildExploited:true'
                }}
                loading={statsLoading}
                customClasses={classes}
            />
        )
    )
);

export {
    WidgetBulletins,
    WidgetCVEPerDay,
    WidgetCVEProgression,
    WidgetEntriesPerDay,
    WidgetExploitAppearHours,
    WidgetExploits,
    WidgetScore,
    WidgetTweets,
    WidgetVendors,
    WidgetWildExploited
};
